import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, cn } from '@scio3/ui';
import { UPDATE_STRIPE_BILLING_INFO } from '@scio3/graphql-client';
import { toast } from 'sonner';
import { COUNTRIES, US_STATES } from '@/features/settings/utils/profile-constants';
import { CountryCombobox, UsStateCombobox } from '@/components/shared/CountryCombobox';
import { loadGoogleMapsScript } from '@/lib/google-maps';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

type BillingAddress = {
  country?: string;
  state?: string;
  postal_code?: string;
  city?: string;
  line1?: string;
  line2?: string;
  phone?: string;
};

type BillingAddressFormProps = {
  /** Initial address values */
  initialValues?: BillingAddress;
  /** Sub-account ID for reseller billing */
  subAccountId?: string;
  /** Team ID for team billing */
  teamId?: string;
  /** Called after successful save */
  onSaved?: () => void;
  /**
   * Skip the view mode and mount straight into the form, with the buttons right-aligned as a
   * dialog footer rather than centred.
   *
   * For callers where opening the form IS the action the user already took — e.g. an
   * "Edit information" dialog. There, view mode was a dead step: you clicked Edit and got
   * "No billing address set" plus an Update Billing Address button, i.e. a second click to reach
   * the thing you asked for. Defaults false, so the inline collapsible is unchanged.
   */
  startInEditMode?: boolean;
  /**
   * Called instead of collapsing back to view mode when Cancel is pressed. Required alongside
   * `startInEditMode`: with no view mode to return to, the default behaviour would leave the
   * caller showing an empty summary inside an open dialog.
   */
  onCancel?: () => void;
};

/**
 * Extract billing address fields from Google Places address_components.
 * Matches scio1's locationAutoComplete.js parsing logic.
 */
function extractAddressComponents(place: google.maps.places.PlaceResult): Partial<BillingAddress> {
  const components = place.address_components ?? [];
  const result: Partial<BillingAddress> = {};

  let countryCode = '';
  let stateCode = '';

  for (const component of components) {
    const types = component.types;
    if (types.includes('country')) {
      countryCode = component.short_name;
    } else if (types.includes('administrative_area_level_1')) {
      stateCode = component.short_name;
      result.state = component.long_name;
    } else if (types.includes('locality') || types.includes('postal_town')) {
      result.city = component.short_name;
    } else if (types.includes('postal_code')) {
      result.postal_code = component.short_name;
    }
  }

  // Map country code to our COUNTRIES list
  if (countryCode) {
    const found = COUNTRIES.find((c) => c.code === countryCode);
    if (found) {
      result.country = found.code;
    }
  }

  // For US, map state to abbreviation matching scio1's US_STATES_PAYMENT lookup
  if (countryCode === 'US' && stateCode) {
    const foundState = US_STATES.find((s) => s.abbreviation === stateCode);
    if (foundState) {
      result.state = foundState.name;
    }
  }

  // Use place name as line1 (street address)
  result.line1 = place.name ?? '';

  return result;
}

/**
 * Billing address form with view/edit modes.
 * Edit mode uses scio1's 2-column grid layout.
 * Address field has Google Places autocomplete (matching scio1).
 * Calls updateStripeBillingInfo mutation on submit.
 */
export function BillingAddressForm({
  initialValues,
  subAccountId,
  teamId,
  onSaved,
  startInEditMode = false,
  onCancel,
}: BillingAddressFormProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [address, setAddress] = useState<BillingAddress>(initialValues ?? {});
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // #1155 — refetch GetStripeCustomerInfo so consumers (e.g. Tax ID gate in
  // PaymentMethodPage) see the new country immediately after save instead of
  // requiring a manual refresh.
  const [updateBillingInfo, { loading }] = useMutation(UPDATE_STRIPE_BILLING_INFO, {
    refetchQueries: ['GetStripeCustomerInfo'],
    awaitRefetchQueries: true,
  });

  // Initialize Google Places autocomplete when editing starts
  useEffect(() => {
    if (!isEditing || !GOOGLE_MAPS_API_KEY || !addressInputRef.current) return;
    if (autocompleteRef.current) return; // Already initialized

    let cancelled = false;
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        // Wait for Autocomplete constructor (async loading may delay it)
        const waitForAutocomplete = () =>
          new Promise<void>((resolve) => {
            if (window.google?.maps?.places?.Autocomplete) {
              resolve();
              return;
            }
            const interval = setInterval(() => {
              if (window.google?.maps?.places?.Autocomplete) {
                clearInterval(interval);
                resolve();
              }
            }, 50);
          });
        return waitForAutocomplete();
      })
      .then(() => {
        if (cancelled || !addressInputRef.current) return;
        const autocomplete = new google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['address'],
          fields: ['address_components', 'name', 'formatted_address'],
        });
        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place?.address_components) return;

          const extracted = extractAddressComponents(place);
          setAddress((prev) => ({
            ...prev,
            ...extracted,
          }));
        });
      })
      .catch(() => {
        // Graceful fallback — autocomplete just won't work
      });

    return () => {
      cancelled = true;
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isEditing]);

  const handleSave = async () => {
    // Required-field validation matching scio1 parity (#46).
    // scio1 blocks save + shows SweetAlert when any of these are missing.
    // `line2` (Address Line 2) and `phone` stay optional.
    const requiredFields: Array<[keyof BillingAddress, string]> = [
      ['line1', t('Address is required')],
      ['city', t('City is required')],
      ['country', t('Country is required')],
      ['state', t('State is required')],
      ['postal_code', t('Zip / Postal Code is required')],
    ];
    for (const [field, message] of requiredFields) {
      if (!(address[field] ?? '').trim()) {
        toast.error(message);
        return;
      }
    }

    try {
      await updateBillingInfo({
        variables: {
          data: address,
          subAccountId,
          teamId,
        },
      });
      toast(t('Your billing address has been saved!'));
      setIsEditing(false);
      onSaved?.();
    } catch {
      toast.error(t('Failed to update billing address'));
    }
  };

  const handleFieldChange = (field: keyof BillingAddress, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setAddress(initialValues ?? {});
    // No view mode to fall back to when the form IS the dialog — hand control to the caller so
    // it can close instead of collapsing to an empty summary.
    if (onCancel) onCancel();
    else setIsEditing(false);
    // Clean up autocomplete
    if (autocompleteRef.current) {
      google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  };

  // Resolve country code to display name
  const countryName = COUNTRIES.find((c) => c.code === address.country)?.name ?? address.country;

  // View mode — summary + "Update Billing Address" button
  if (!isEditing) {
    const addressParts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postal_code,
      countryName,
    ].filter(Boolean);
    const hasAddress = addressParts.length > 0;

    return (
      <div className="space-y-4" data-testid="payment-billing-address-view">
        <div className="text-sm text-muted-foreground space-y-1">
          {hasAddress ? (
            <>
              <p data-testid="payment-billing-address-summary">{addressParts.join(', ')}</p>
              {address.phone && <p>{address.phone}</p>}
            </>
          ) : (
            <p className="italic">{t('No billing address set')}</p>
          )}
        </div>
        <div className="flex justify-center">
          <Button
            onClick={() => setIsEditing(true)}
            className="cursor-pointer"
            data-testid="payment-billing-address-update-btn"
          >
            {t('Update Billing Address')}
          </Button>
        </div>
      </div>
    );
  }

  // Edit mode — 2-column grid matching scio1 layout
  return (
    <div className="space-y-5" data-testid="payment-billing-address-form">
      {/* Row 1: Address + Address Line 2 */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="billing-line1">{t('Address')}:</Label>
          <Input
            ref={addressInputRef}
            id="billing-line1"
            data-testid="payment-billing-address-line1"
            value={address.line1 ?? ''}
            onChange={(e) => handleFieldChange('line1', e.target.value)}
            placeholder={t('exp : 123 Main St, Houston, TX')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="billing-line2">
            {t('Address Line 2')} ({t('Optional')}):
          </Label>
          <Input
            id="billing-line2"
            data-testid="payment-billing-address-line2"
            value={address.line2 ?? ''}
            onChange={(e) => handleFieldChange('line2', e.target.value)}
          />
        </div>
      </div>

      {/* Row 2: City + Country (dropdown) */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="billing-city">{t('City')}:</Label>
          <Input
            id="billing-city"
            data-testid="payment-billing-address-city"
            value={address.city ?? ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="billing-country">{t('Country')}:</Label>
          <CountryCombobox
            id="billing-country"
            value={address.country ?? 'US'}
            onChange={(v) => {
              handleFieldChange('country', v);
              if (v !== 'US') handleFieldChange('state', '');
            }}
            testIdPrefix="payment-billing-address-country"
          />
        </div>
      </div>

      {/* Row 3: State + Zip */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="billing-state">{t('State')}:</Label>
          {(address.country ?? 'US') === 'US' ? (
            <UsStateCombobox
              id="billing-state"
              // Billing Address stores the full state name (Order Devices stores the abbreviation).
              valueKind="name"
              value={address.state ?? ''}
              onChange={(v) => handleFieldChange('state', v)}
              testIdPrefix="payment-billing-address-state"
            />
          ) : (
            <Input
              id="billing-state"
              data-testid="payment-billing-address-state-input"
              value={address.state ?? ''}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              placeholder={t('Region/State')}
            />
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="billing-zip">{t('Zip / Postal Code')}:</Label>
          <Input
            id="billing-zip"
            data-testid="payment-billing-address-zip"
            value={address.postal_code ?? ''}
            onChange={(e) => handleFieldChange('postal_code', e.target.value)}
          />
        </div>
      </div>

      {/* Row 4: Phone (full width) */}
      <div className="space-y-2">
        <Label htmlFor="billing-phone">{t('Phone')}:</Label>
        <Input
          id="billing-phone"
          data-testid="payment-billing-address-phone"
          type="tel"
          value={address.phone ?? ''}
          onChange={(e) => handleFieldChange('phone', e.target.value)}
        />
      </div>

      {/* Centred to match scio1 inline; right-aligned in a dialog, where the same two buttons
          read as a footer. */}
      <div className={cn('flex gap-2 pt-2', startInEditMode ? 'justify-end' : 'justify-center')}>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="cursor-pointer"
          data-testid="payment-billing-address-save-btn"
        >
          {loading ? t('Saving...') : t('Save')}
        </Button>
        <Button
          variant="outline"
          onClick={handleCancel}
          className="cursor-pointer"
          data-testid="payment-billing-address-cancel-btn"
        >
          {t('Cancel')}
        </Button>
      </div>
    </div>
  );
}
